/**
 * PodcastSubscribeRow — "Listen on Apple Podcasts / Spotify" badges
 * shown under every story player. Drives discovery and subscriber
 * growth: a casual reader who finishes a story is the warmest possible
 * lead for a podcast subscription. One tap from this badge → they're
 * subscribed for life.
 *
 * The Apple URL is left blank until Apple finishes processing the
 * first-time submission (usually 4-24h). Show the Spotify badge
 * standalone in the meantime; flip the Apple one on the moment Apple
 * approves by setting APPLE_URL below.
 */
import React from "react";

// Public Spotify show URL — pulled from the user's Spotify for Creators
// dashboard after their RSS feed was approved. If you ever change podcast
// hosting / re-submit, paste the new show ID here.
const SPOTIFY_URL = "https://open.spotify.com/show/033xKhgIPt6oQg3YcdCAQa";

// Set this to your Apple Podcasts URL after submission is approved
// (it'll look like https://podcasts.apple.com/gb/podcast/storylo.../id...).
// Until then the Apple badge stays hidden — no broken links.
const APPLE_URL = "";

export default function PodcastSubscribeRow({ compact = false }) {
  if (!SPOTIFY_URL && !APPLE_URL) return null;

  return (
    <div
      className="rounded-xl border border-white/8 bg-white/[0.02] px-4 py-3.5"
      data-testid="podcast-subscribe-row"
    >
      <div className="text-[11px] uppercase tracking-[0.22em] text-[color:var(--muted)] mb-2.5 flex items-center gap-2">
        <span
          aria-hidden="true"
          className="inline-block w-1.5 h-1.5 rounded-full bg-[color:var(--accent)]"
        />
        Listen to every Storylo story as a podcast
      </div>
      <div className={`flex flex-wrap items-center gap-2.5 ${compact ? "" : "md:gap-3"}`}>
        {SPOTIFY_URL && (
          <a
            href={SPOTIFY_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-flex items-center gap-2.5 pl-3 pr-4 py-2 rounded-full transition-transform hover:scale-[1.025] active:scale-[0.99]"
            style={{
              background: "#1DB954",
              color: "#04150d",
              boxShadow: "0 1px 0 rgba(255,255,255,0.18) inset, 0 6px 20px -8px rgba(29, 185, 84, 0.55)",
            }}
            data-testid="subscribe-spotify"
            aria-label="Listen on Spotify"
          >
            {/* Spotify logo (official mark, simplified inline SVG so we
                don't have to bundle anything extra). */}
            <svg
              width="18" height="18" viewBox="0 0 24 24"
              fill="currentColor" aria-hidden="true"
            >
              <path d="M12 0a12 12 0 1 0 0 24 12 12 0 0 0 0-24Zm5.503 17.302a.747.747 0 0 1-1.027.248c-2.815-1.72-6.358-2.108-10.532-1.155a.748.748 0 0 1-.332-1.458c4.572-1.04 8.51-.593 11.643 1.336.355.217.467.683.248 1.029Zm1.47-3.272a.935.935 0 0 1-1.285.308c-3.224-1.982-8.137-2.557-11.948-1.397a.935.935 0 1 1-.547-1.788c4.354-1.327 9.771-.685 13.471 1.594.44.273.581.851.31 1.283Zm.127-3.408C15.245 8.394 8.41 8.16 4.683 9.291a1.122 1.122 0 0 1-.65-2.148c4.281-1.296 11.829-1.022 16.499 1.755a1.121 1.121 0 1 1-1.43 1.724l-.002-.001Z" />
            </svg>
            <span className="text-sm font-semibold tracking-tight">
              Listen on Spotify
            </span>
          </a>
        )}

        {APPLE_URL && (
          <a
            href={APPLE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-flex items-center gap-2.5 pl-3 pr-4 py-2 rounded-full transition-transform hover:scale-[1.025] active:scale-[0.99]"
            style={{
              background: "#000",
              color: "#fff",
              boxShadow: "0 1px 0 rgba(255,255,255,0.10) inset, 0 6px 20px -8px rgba(167, 99, 240, 0.45)",
            }}
            data-testid="subscribe-apple"
            aria-label="Listen on Apple Podcasts"
          >
            {/* Apple Podcasts logo. */}
            <svg
              width="18" height="18" viewBox="0 0 24 24"
              fill="currentColor" aria-hidden="true"
            >
              <path d="M21.5 6.6a9.61 9.61 0 1 0-15.36 11.5 .87.87 0 0 0 1.18-.07.87.87 0 0 0-.07-1.23A7.86 7.86 0 1 1 18 16.83a.87.87 0 1 0 1.1 1.34A9.6 9.6 0 0 0 21.5 6.6Zm-9.5 1.5a4.4 4.4 0 0 0-2.81 7.78.87.87 0 0 0 1.12-1.33 2.66 2.66 0 1 1 3.38 0 .87.87 0 0 0 1.12 1.33A4.4 4.4 0 0 0 12 8.1Zm0 4.4a1.5 1.5 0 0 0-1.5 1.5c0 .56.31 1.05.77 1.31l-.51 5.34a1.24 1.24 0 0 0 1.24 1.35h0a1.24 1.24 0 0 0 1.24-1.35l-.51-5.34a1.5 1.5 0 0 0-.73-2.81Z" />
            </svg>
            <span className="text-sm font-semibold tracking-tight">
              Listen on Apple Podcasts
            </span>
          </a>
        )}

        {!APPLE_URL && (
          <span
            className="text-[11px] text-[color:var(--muted)] opacity-70 italic"
            data-testid="subscribe-apple-pending"
          >
            Apple Podcasts coming soon
          </span>
        )}
      </div>
    </div>
  );
}
